<template>
  <div class="notes-container-desktop eh-ds">
    <div class="notes-header-desktop">NOTES</div>

    <div class="dropdown-container-desktop">
      <div class="note-type-section-desktop">
        <select v-model="selectedNoteType" @change="handleFilterChange">
          <option value="">Select note</option>
          <option v-for="type in uniqueAccountTypes" :key="type" :value="type">
            {{ formatLabel(type) }}
          </option>
          <option value="show-all">Show All</option>
        </select>
      </div>
      <div class="note-type-section-desktop">
        <select v-model="selectedNoteCategory" @change="handleFilterChange">
          <option value="">Select type</option>
          <option v-for="category in uniqueTypes" :key="category" :value="category">
            {{ formatTypeLabel(category) }}
          </option>
          <option value="show-all">Show All</option>
        </select>
      </div>
    </div>

    <div class="date-range-row">
      <div class="note-type-section-desktop">
        <select v-model="selectedDateRange" @change="handleFilterChange">
          <option value="">Select dates</option>
          <option value="lastWeek">Last week</option>
          <option value="last2Weeks">Last 2 weeks</option>
          <option value="last30Days">Last 30 days</option>
          <option value="last90Days">Last 90 days</option>
          <option value="last365Days">Last 365 days</option>
          <option value="all">All</option>
        </select>
      </div>
      <div class="or-text-desktop">OR</div>
      <div class="date-range-section-desktop">
        <flat-pickr
          v-model="customDateRange"
          :config="flatpickrConfig"
          placeholder="Select date range"
          @on-change="handleCustomDateChange"
        />
      </div>
    </div>

    <div class="filters-actions">
      <button
        type="button"
        class="btn-outline filters-clear-btn"
        :disabled="!hasActiveFilters"
        @click="clearFilters"
      >
        Clear
      </button>
    </div>

    <div v-for="patient in sortedPatients" :key="patient.patient_id" class="patient-section">
      <div class="patient-header">
        <h2 class="patient-header__name">{{ patientDisplayName(patient) }}</h2>
        <!-- Banner toggle: expand/collapse older note dates without scrolling to the list bottom. -->
        <button
          v-if="getAdditionalDateKeys(patient.patient_id).length > 0"
          type="button"
          class="btn-outline patient-header__toggle"
          @click="toggleAdditionalNotes(patient.patient_id)"
        >
          {{ isAdditionalExpanded(patient.patient_id) ? "Show less" : "Show more" }}
        </button>
      </div>

      <div class="notes-display-desktop">
        <template v-if="hasNotesForPatient(patient.patient_id)">
          <div
            v-for="date in visibleDateKeysForPatient(patient.patient_id)"
            :key="date"
            class="note-entry"
          >
            <button
              class="date-button-desktop"
              :class="{ 'active-date-button-desktop': expandedKey === sectionKey(patient.patient_id, date) }"
              @click="toggleNoteForDate(patient.patient_id, date)"
            >
              {{ formatDisplayDate(date) }}
            </button>
            <div
              v-if="expandedKey === sectionKey(patient.patient_id, date)"
              class="note-content-desktop"
            >
              <div class="note-tabs-wrapper-desktop">
                <div class="note-tabs-desktop">
                  <button
                    v-for="(writer, index) in writers"
                    :key="index"
                    class="tab-desktop"
                    :class="{
                      'active-tab-desktop': activeTab === index,
                      'inactive-tab-desktop': activeTab !== index,
                    }"
                    @click="selectWriterTab(index)"
                  >
                    {{ writer }}
                  </button>
                </div>
              </div>
              <div class="note-content-inner-desktop">
                <div v-if="currentNote">
                  <h4 v-if="currentNote.startTime && currentNote.endTime">
                    {{ formatTimeRange(currentNote.startTime, currentNote.endTime) }}
                  </h4>
                  <h4 v-else-if="currentNote.startTime || currentNote.endTime">
                    {{ formatPartialTime(currentNote.startTime, currentNote.endTime) }}
                  </h4>
                  <h4 v-else>No time specified</h4>
                  <p v-html="currentNote.pnotes"></p>
                  <note-attachment-list :note="currentNote" />
                </div>
              </div>
              <div class="pagination-desktop" v-if="totalPages > 1">
                <button class="prev-page-desktop" :disabled="currentPage === 1" @click="prevPage">
                  &#8249;
                </button>
                <span class="page-indicator-desktop">{{ currentPage }} / {{ totalPages }}</span>
                <button
                  class="next-page-desktop"
                  :disabled="currentPage === totalPages"
                  @click="nextPage"
                >
                  &#8250;
                </button>
              </div>
            </div>
          </div>
        </template>
        <p v-else class="no-notes">No notes for this patient.</p>
      </div>
    </div>

    <p v-if="sortedPatients.length === 0" class="no-notes">No patients found.</p>
  </div>
</template>

<script>
import { CURR_API } from "@/constants/apis";
import { mapGetters } from "vuex";
import axios from "axios";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import NoteAttachmentList from "@/components/includes/attachments/NoteAttachmentList.vue";
export default {
  name: "ProfessionalNotesPage",
  components: { flatPickr, NoteAttachmentList },
  data() {
    return {
      patients: [],
      notesByPatient: {},
      filteredNotesByPatient: {},
      selectedNoteType: "",
      selectedNoteCategory: "",
      selectedDateRange: "",
      customDateRange: null,
      startDate: null,
      endDate: null,
      uniqueAccountTypes: [],
      uniqueTypes: [],
      expandedKey: null,
      expandedAdditionalByPatient: {},
      writers: [],
      currentNote: null,
      currentPage: 1,
      totalPages: 1,
      activeTab: 0,
      flatpickrConfig: {
        dateFormat: "Y-m-d",
        mode: "range",
        altInput: true,
        altFormat: "F j, Y",
      },
    };
  },
  computed: {
    ...mapGetters("login", { allData: "getAllData" }),
    sortedPatients() {
      return [...this.patients].sort((a, b) => {
        const latestA = this.getMostRecentNote(a.patient_id);
        const latestB = this.getMostRecentNote(b.patient_id);
        if (!latestA && !latestB) {
          return this.patientDisplayName(a).localeCompare(this.patientDisplayName(b));
        }
        if (!latestA) return 1;
        if (!latestB) return -1;
        return this.compareNotes(latestA, latestB);
      });
    },
    // True when any note/type/date filter is set (enables Clear).
    hasActiveFilters() {
      return !!(
        this.selectedNoteType ||
        this.selectedNoteCategory ||
        this.selectedDateRange ||
        this.customDateRange ||
        this.startDate ||
        this.endDate
      );
    },
  },
  mounted() {
    this.loadPatientsAndNotes();
  },
  methods: {
    async loadPatientsAndNotes() {
      const accountnumber = this.allData.patient.externId2;
      const subaccountnumber = this.allData.patient.externId;

      const typeRes = await axios.post(
        CURR_API + "/tswebhook.php",
        {
          Account: {
            API_Meth: "GetAccountType",
            accountnumber,
            subaccountnumber,
          },
        },
        { headers: { "Content-Type": "application/json;" } }
      );

      // `account_type` arrives from the ported API as a snake_case slug
      // (`registered_nurse`); the list below is the legacy spaced English.
      // Without folding underscores no nurse, aide or allied professional
      // matched, `ownerId` fell through to "", and the caseload query returned
      // every patient in the organization instead of their own.
      const accounttype = (typeRes.data?.providerAssignment?.accounttype || "")
        .toLowerCase()
        .replace(/_/g, " ");
      const staffTypes = [
        "provider", "nurse", "registered nurse", "licensed practical nurse",
        "home health aid", "home health aids", "home health aide", "home health aides",
        "allied professional", "allied professionals",
      ];
      const ownerId = staffTypes.includes(accounttype)
        ? String(this.allData.patient.externId || this.allData.patient.externid || "").trim()
        : "";

      const patientsRes = await axios.post(
        CURR_API + "/tswebhook.php",
        {
          Groups: {
            API_Meth: "GetPatientsForHomeGrid",
            accountnumber,
            owner_unique_ID: ownerId || null,
          },
        },
        { headers: { "Content-Type": "application/json;" } }
      );

      const rows = Array.isArray(patientsRes?.data?.data) ? patientsRes.data.data : [];
      this.patients = rows.map((r) => ({
        ...r,
        patient_id: String(r.patient_id || r.mid || r.unique_ID || "").trim(),
      }));

      for (const patient of this.patients) {
        const res = await axios.post(
          `${CURR_API}/tswebhook.php`,
          {
            Note: {
              API_Meth: "GetAllNotes",
              patientID: patient.patient_id,
            },
          },
          { headers: { "Content-Type": "application/json;" } }
        );
        this.notesByPatient[patient.patient_id] = res.data.data || [];
      }
      this.populateUniqueOptions();
      this.applyFilters();
    },
    patientDisplayName(patient) {
      const first = patient.first_name || "";
      const last = patient.lastname || patient.last_name || "";
      return `${first} ${last}`.trim() || patient.patient_id;
    },
    getNotesByDate(patientId) {
      const grouped = this.filteredNotesByPatient[patientId];
      return grouped && typeof grouped === "object" && !Array.isArray(grouped) ? grouped : {};
    },
    hasNotesForPatient(patientId) {
      return this.getSortedDateKeys(patientId).length > 0;
    },
    getLatestNoteInGroup(notes) {
      const list = Array.isArray(notes) ? [...notes] : [];
      list.sort((a, b) => this.compareNotes(a, b));
      return list[0] || null;
    },
    getSortedDateKeys(patientId) {
      const notesByDate = this.getNotesByDate(patientId);
      return Object.keys(notesByDate).sort((dateA, dateB) => {
        const latestA = this.getLatestNoteInGroup(notesByDate[dateA]);
        const latestB = this.getLatestNoteInGroup(notesByDate[dateB]);
        if (!latestA && !latestB) return 0;
        if (!latestA) return 1;
        if (!latestB) return -1;
        return this.compareNotes(latestA, latestB);
      });
    },
    getMostRecentNote(patientId) {
      const dates = this.getSortedDateKeys(patientId);
      if (!dates.length) return null;
      return this.getLatestNoteInGroup(this.getNotesByDate(patientId)[dates[0]]);
    },
    getAdditionalDateKeys(patientId) {
      const dates = this.getSortedDateKeys(patientId);
      return dates.length > 1 ? dates.slice(1) : [];
    },
    visibleDateKeysForPatient(patientId) {
      const dates = this.getSortedDateKeys(patientId);
      if (!dates.length) return [];
      if (this.isAdditionalExpanded(patientId)) return dates;
      return [dates[0]];
    },
    compareNotes(a, b) {
      const dateA = this.parseLocalDate(a.postDate);
      const dateB = this.parseLocalDate(b.postDate);
      const timeA = dateA ? dateA.getTime() : 0;
      const timeB = dateB ? dateB.getTime() : 0;
      if (timeB !== timeA) return timeB - timeA;
      return Number(b.pid || 0) - Number(a.pid || 0);
    },
    sectionKey(patientId, date) {
      return `${patientId}|${date}`;
    },
    parseSectionKey(key) {
      const [patientId, ...dateParts] = String(key || "").split("|");
      return { patientId, date: dateParts.join("|") };
    },
    isAdditionalExpanded(patientId) {
      return !!this.expandedAdditionalByPatient[patientId];
    },
    // Banner "Show more" / "Show less" for this patient's older note dates.
    toggleAdditionalNotes(patientId) {
      if (this.isAdditionalExpanded(patientId)) {
        this.collapseAdditionalNotes(patientId);
      } else {
        this.expandAdditionalNotes(patientId);
      }
    },
    expandAdditionalNotes(patientId) {
      this.expandedAdditionalByPatient = {
        ...this.expandedAdditionalByPatient,
        [patientId]: true,
      };
    },
    collapseAdditionalNotes(patientId) {
      const next = { ...this.expandedAdditionalByPatient };
      delete next[patientId];
      this.expandedAdditionalByPatient = next;

      const { patientId: openPatientId, date } = this.parseSectionKey(this.expandedKey);
      const mostRecentDate = this.getSortedDateKeys(patientId)[0] || "";
      if (openPatientId === patientId && date && date !== mostRecentDate) {
        this.expandedKey = null;
        this.currentNote = null;
      }
    },
    toggleNoteForDate(patientId, date) {
      const key = this.sectionKey(patientId, date);
      if (this.expandedKey === key) {
        this.expandedKey = null;
        this.currentNote = null;
        return;
      }
      this.expandedKey = key;
      this.currentPage = 1;
      this.activeTab = 0;
      this.writers = this.getWritersForDate(patientId, date);
      if (this.writers.length > 0) this.selectWriterTab(0);
    },
    getWritersForDate(patientId, date) {
      const notes = this.getNotesByDate(patientId)[date] || [];
      return Array.from(new Set(notes.map((note) => note.name)));
    },
    selectWriterTab(index) {
      this.activeTab = index;
      this.currentPage = 1;
      const { patientId, date } = this.parseSectionKey(this.expandedKey);
      const writer = this.writers[index];
      const writerNotes = this.getNotesForWriter(patientId, date, writer);
      this.totalPages = writerNotes.length;
      this.currentNote = writerNotes[0] || null;
    },
    getNotesForWriter(patientId, date, writer) {
      const notes = this.getNotesByDate(patientId)[date] || [];
      return notes
        .filter((note) => note.name === writer)
        .sort((a, b) => this.compareNotes(a, b));
    },
    prevPage() {
      if (this.currentPage <= 1) return;
      this.currentPage--;
      const { patientId, date } = this.parseSectionKey(this.expandedKey);
      const writer = this.writers[this.activeTab];
      const writerNotes = this.getNotesForWriter(patientId, date, writer);
      this.currentNote = writerNotes[this.currentPage - 1] || null;
    },
    nextPage() {
      if (this.currentPage >= this.totalPages) return;
      this.currentPage++;
      const { patientId, date } = this.parseSectionKey(this.expandedKey);
      const writer = this.writers[this.activeTab];
      const writerNotes = this.getNotesForWriter(patientId, date, writer);
      this.currentNote = writerNotes[this.currentPage - 1] || null;
    },
    handleFilterChange() {
      // Preset dropdown and custom range are mutually exclusive.
      if (this.selectedDateRange) {
        this.customDateRange = null;
        this.startDate = null;
        this.endDate = null;
      }
      this.expandedKey = null;
      this.expandedAdditionalByPatient = {};
      this.currentPage = 1;
      this.activeTab = 0;
      this.currentNote = null;
      this.applyFilters();
    },
    // Reset every filter control and show the full unfiltered note set again.
    clearFilters() {
      this.selectedNoteType = "";
      this.selectedNoteCategory = "";
      this.selectedDateRange = "";
      this.customDateRange = null;
      this.startDate = null;
      this.endDate = null;
      this.expandedKey = null;
      this.expandedAdditionalByPatient = {};
      this.currentPage = 1;
      this.activeTab = 0;
      this.currentNote = null;
      this.applyFilters();
    },
    applyFilters() {
      for (const patient of this.patients) {
        let tempNotes = [...(this.notesByPatient[patient.patient_id] || [])];

        if (this.selectedNoteType && this.selectedNoteType !== "show-all") {
          tempNotes = tempNotes.filter((note) => note.accountType === this.selectedNoteType);
        }
        if (this.selectedNoteCategory && this.selectedNoteCategory !== "show-all") {
          tempNotes = tempNotes.filter((note) => note.type === this.selectedNoteCategory);
        }
        if (this.selectedDateRange) {
          // Compare calendar days only so "last N days" is not skewed by time-of-day.
          const today = this.startOfLocalDay(new Date());
          let startDate;
          switch (this.selectedDateRange) {
            case "lastWeek":
              startDate = this.startOfLocalDay(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000));
              break;
            case "last2Weeks":
              startDate = this.startOfLocalDay(new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000));
              break;
            case "last30Days":
              startDate = this.startOfLocalDay(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000));
              break;
            case "last90Days":
              startDate = this.startOfLocalDay(new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000));
              break;
            case "last365Days":
              startDate = this.startOfLocalDay(new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000));
              break;
            case "all":
              startDate = null;
              break;
            default:
              startDate = today;
          }
          if (startDate) {
            tempNotes = tempNotes.filter((note) => {
              const noteDate = this.parseLocalDate(note.endDt || note.postDate);
              if (!noteDate) return false;
              return noteDate >= startDate && noteDate <= today;
            });
          }
        }
        if (this.customDateRange && this.startDate && this.endDate) {
          const startDateObj = this.parseLocalDate(this.startDate);
          const endDateObj = this.parseLocalDate(this.endDate);
          tempNotes = tempNotes.filter((note) => {
            const noteDate = this.parseLocalDate(note.endDt || note.postDate);
            if (!noteDate || !startDateObj || !endDateObj) return false;
            return noteDate >= startDateObj && noteDate <= endDateObj;
          });
        }

        const notesByDate = {};
        tempNotes.forEach((note) => {
          const dateKey = note.endDt || note.postDate;
          if (!dateKey) return;
          if (!notesByDate[dateKey]) notesByDate[dateKey] = [];
          notesByDate[dateKey].push(note);
        });
        this.filteredNotesByPatient[patient.patient_id] = notesByDate;
      }
    },
    handleCustomDateChange(selectedDatesArray) {
      const dates = Array.isArray(selectedDatesArray) ? selectedDatesArray : [];
      if (!this.customDateRange || dates.length === 0) {
        this.startDate = null;
        this.endDate = null;
        this.applyFilters();
        return;
      }
      this.selectedDateRange = "";
      // Flatpickr on-change gives Date objects (not YYYY-MM-DD strings).
      if (dates.length === 1) {
        this.startDate = dates[0];
        this.endDate = dates[0];
      } else {
        this.startDate = dates[0];
        this.endDate = dates[1];
      }
      this.handleFilterChange();
    },
    populateUniqueOptions() {
      const accountTypes = new Set();
      const types = new Set();
      Object.values(this.notesByPatient).forEach((notes) => {
        notes.forEach((note) => {
          if (note.accountType) accountTypes.add(note.accountType);
          if (note.type) types.add(note.type);
        });
      });
      this.uniqueAccountTypes = Array.from(accountTypes);
      this.uniqueTypes = Array.from(types);
    },
    // Midnight local for calendar-day comparisons.
    startOfLocalDay(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },
    // Accept Date (flatpickr) or YYYY-MM-DD string (note fields); always return local midnight.
    parseLocalDate(value) {
      if (value == null || value === "") return null;
      if (value instanceof Date) return this.startOfLocalDay(value);
      const dateString = String(value).trim();
      if (!dateString) return null;
      // Prefer the date portion when the API sends "YYYY-MM-DD HH:mm:ss".
      const ymd = dateString.slice(0, 10);
      const dateParts = ymd.split("-");
      if (dateParts.length === 3 && dateParts.every((p) => /^\d+$/.test(p))) {
        return new Date(
          parseInt(dateParts[0], 10),
          parseInt(dateParts[1], 10) - 1,
          parseInt(dateParts[2], 10)
        );
      }
      const parsed = new Date(dateString);
      return this.startOfLocalDay(parsed);
    },
    formatDisplayDate(date) {
      const localDate = this.parseLocalDate(date);
      return localDate
        ? localDate.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
        : date;
    },
    formatTimeRange(startTime, endTime) {
      return `${this.formatToAMPM(startTime)} - ${this.formatToAMPM(endTime)}`;
    },
    formatPartialTime(startTime, endTime) {
      if (startTime && endTime) return this.formatTimeRange(startTime, endTime);
      if (startTime) return `${this.formatToAMPM(startTime)} - `;
      if (endTime) return ` - ${this.formatToAMPM(endTime)}`;
      return "No time specified";
    },
    formatToAMPM(time) {
      if (!time) return "";
      let [hour, minute] = time.split(":").map(Number);
      const ampm = hour >= 12 ? "PM" : "AM";
      hour = hour % 12 || 12;
      return `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
    },
    formatLabel(value) {
      return value
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    formatTypeLabel(value) {
      if (value === "9945X" || value === "9898X") return `Vitals - ${value}`;
      return this.formatLabel(value);
    },
  },
};
</script>

<style scoped>
.notes-container-desktop {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.notes-header-desktop {
  background-color: #0c8687;
  color: white;
  text-align: center;
  padding: 20px;
  border-radius: 5px;
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 25px;
}

.patient-section {
  margin-top: 30px;
}

.patient-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background-color: #e8f4f4;
  color: #0c8687;
  padding: 12px 20px;
  border-radius: 5px;
  margin-bottom: 15px;
  border-left: 4px solid #0c8687;
}

.patient-header__name {
  margin: 0;
  font-size: 20px;
  font-weight: bold;
  color: inherit;
  min-width: 0;
}

.patient-header__toggle {
  flex-shrink: 0;
  margin-left: auto;
  padding: 6px 14px;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.no-notes {
  color: #666;
  padding: 10px 20px;
  font-style: italic;
}

.dropdown-container-desktop {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin: 20px 0;
  padding: 0 20px;
}

.note-type-section-desktop {
  flex: 1;
}

.date-range-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 20px;
}

.filters-actions {
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px 0;
}

.filters-clear-btn {
  padding: 8px 16px;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
}

.filters-clear-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.date-range-section-desktop {
  flex: 1;
}

.or-text-desktop {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.note-type-section-desktop select,
.date-range-section-desktop :deep(input) {
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: #fff;
}

.notes-display-desktop {
  margin-top: 15px;
  padding: 0 20px;
}

.date-button-desktop {
  background-color: #0c8687;
  color: white;
  padding: 12px 20px;
  border-radius: 5px;
  border: none;
  width: 100%;
  margin-bottom: 12px;
  text-align: left;
  font-size: 16px;
  cursor: pointer;
}

.date-button-desktop:hover,
.active-date-button-desktop {
  background-color: #086d68;
}

.note-entry + .note-entry {
  margin-top: 0;
}


.note-content-desktop {
  background-color: #f9f9f9;
  padding: 20px;
  padding-bottom: 60px;
  border-radius: 5px;
  min-height: 100px;
  max-height: 600px;
  overflow: hidden;
  margin-bottom: 20px;
  border: 1px solid #ccc;
  position: relative;
}

.note-content-inner-desktop {
  overflow-y: auto;
  max-height: calc(100% - 60px);
}

.note-tabs-wrapper-desktop {
  white-space: nowrap;
  margin-bottom: 10px;
  padding-bottom: 10px;
}

.note-tabs-desktop {
  display: inline-flex;
  gap: 10px;
}

.tab-desktop {
  background-color: #cccccc;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  border: none;
}

.inactive-tab-desktop {
  opacity: 0.7;
}

.active-tab-desktop {
  background-color: #0c8687;
  opacity: 1;
}

.pagination-desktop {
  position: absolute;
  bottom: 10px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
}

.pagination-desktop button {
  background-color: #086d68;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 5px;
  cursor: pointer;
}

.pagination-desktop button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
